import {parseEnumAttribute} from "../shared/blockAttributes";

// A4 portrait content box is ~184 × 265 mm inside the page margins; the caps keep any image on one page.
export const MIN_PRINTOUT_IMAGE_WIDTH_MM = 10;
export const MAX_PRINTOUT_IMAGE_WIDTH_MM = 180;
export const DEFAULT_PRINTOUT_IMAGE_WIDTH_MM = 80;
export const MIN_PRINTOUT_IMAGE_HEIGHT_MM = 10;
export const MAX_PRINTOUT_IMAGE_HEIGHT_MM = 250;
export const DEFAULT_PRINTOUT_IMAGE_HEIGHT_MM = 40;

export const PRINTOUT_IMAGE_ALIGNMENTS = ["left", "center", "right"] as const;
export type PrintoutImageAlignment = typeof PRINTOUT_IMAGE_ALIGNMENTS[number];

export const PRINTOUT_IMAGE_FITS = ["contain", "cover"] as const;
export type PrintoutImageFit = typeof PRINTOUT_IMAGE_FITS[number];

export interface PrintoutImageNodeAttrs {
    alignment?: PrintoutImageAlignment;
    fit?: PrintoutImageFit;
    heightMm?: number;
    publicId?: string | null;
    src?: string | null;
    widthMm?: number;
}

const clampNumber = (value: unknown, fallback: number, min: number, max: number): number => {
    const numberValue = typeof value === "number" ? value : Number(value);

    if (!Number.isFinite(numberValue)) return fallback;

    return Math.min(max, Math.max(min, Math.round(numberValue)));
};

export const clampPrintoutImageWidthMm = (value: unknown, fallback = DEFAULT_PRINTOUT_IMAGE_WIDTH_MM) => (
    clampNumber(value, fallback, MIN_PRINTOUT_IMAGE_WIDTH_MM, MAX_PRINTOUT_IMAGE_WIDTH_MM)
);

export const clampPrintoutImageHeightMm = (value: unknown, fallback = DEFAULT_PRINTOUT_IMAGE_HEIGHT_MM) => (
    clampNumber(value, fallback, MIN_PRINTOUT_IMAGE_HEIGHT_MM, MAX_PRINTOUT_IMAGE_HEIGHT_MM)
);

export const parsePrintoutImageAlignment = (value: unknown): PrintoutImageAlignment => (
    parseEnumAttribute(value, PRINTOUT_IMAGE_ALIGNMENTS, "center")
);

export const parsePrintoutImageFit = (value: unknown): PrintoutImageFit => (
    parseEnumAttribute(value, PRINTOUT_IMAGE_FITS, "contain")
);

export const getPrintoutImageAttrs = (attrs: Partial<PrintoutImageNodeAttrs> = {}): Required<PrintoutImageNodeAttrs> => ({
    alignment: parsePrintoutImageAlignment(attrs.alignment),
    fit: parsePrintoutImageFit(attrs.fit),
    heightMm: clampPrintoutImageHeightMm(attrs.heightMm),
    publicId: typeof attrs.publicId === "string" && attrs.publicId ? attrs.publicId : null,
    src: typeof attrs.src === "string" && attrs.src ? attrs.src : null,
    widthMm: clampPrintoutImageWidthMm(attrs.widthMm),
});
